import { APICore } from './apiCore';

const api = new APICore();

// ─── Helper to get workspace header ───────────────────────────────
const workspaceHeader = (workspaceId?: number | string) =>
    workspaceId ? { headers: { 'X-Workspace-ID': String(workspaceId) } } : {};

// ─── Workspace CRUD ───────────────────────────────────────────────

/** List all workspaces for the authenticated user */
function getWorkspaces() {
    return api.get('/workspaces');
}

/** Create a new business workspace */
function createWorkspace(data: { workspace_name: string; workspace_slug?: string }) {
    return api.create('/workspaces', data);
}

/** Create the user's personal (single-user) workspace */
function createPersonalWorkspace(workspaceName: string) {
    return api.create('/workspaces/create-personal', { workspace_name: workspaceName });
}

/** Switch to a different workspace */
function switchWorkspace(workspaceId: number) {
    return api.create('/workspaces/switch', { workspace_id: workspaceId });
}

/** Get the current active workspace (requires X-Workspace-ID header) */
function getCurrentWorkspace(workspaceId: number | string) {
    return api.get('/workspaces/current', undefined);
}

// ─── Members ──────────────────────────────────────────────────────

/** Get members list for a workspace */
function getMembers(workspaceId: number | string) {
    return api.get('/workspaces/members');
}

/** Invite a user to the workspace */
function inviteMember(data: { email: string; role: string }, workspaceId: number | string) {
    return api.create('/workspaces/invite', data);
}

/** Remove a member from the workspace */
function removeMember(userId: number) {
    return api.delete(`/workspaces/member/${userId}`);
}

/** Update a member's role */
function updateMemberRole(data: { user_id: number; role: string }) {
    return api.create('/workspaces/member/role', data);
}

/** Cancel a pending invitation */
function cancelInvitation(invitationId: number) {
    return api.delete(`/workspaces/invitation/${invitationId}`);
}

// ─── Invitations ──────────────────────────────────────────────────

/** Accept a workspace invitation via token */
function acceptInvitation(token: string) {
    return api.get(`/invitations/accept/${token}`);
}

export {
    getWorkspaces,
    createWorkspace,
    createPersonalWorkspace,
    switchWorkspace,
    getCurrentWorkspace,
    getMembers,
    inviteMember,
    removeMember,
    updateMemberRole,
    cancelInvitation,
    acceptInvitation,
};

// ─── Custom roles ─────────────────────────────────────────────────

export interface RoleItem {
    key: string;
    label: string;
    actions: string[];
    labels?: Record<string, string>;
}

export interface RoleSection {
    key: string;
    label: string;
    items: RoleItem[];
}

export interface CustomRole {
    id: number;
    name: string;
    description?: string | null;
    permissions: Record<string, string[]>;
    members?: number;
}

/** The custom roles of the active workspace and the sections/items/actions a role is built from */
export async function getRoles(): Promise<{ roles: CustomRole[]; catalog: RoleSection[]; actions: Record<string, string> }> {
    const res = await api.get('/workspaces/roles', {});
    const d = res.data || {};
    const fix = (r: any): CustomRole => ({ ...r, permissions: Array.isArray(r.permissions) ? {} : r.permissions || {} });

    return { roles: (d.roles || []).map(fix), catalog: d.catalog || [], actions: d.actions || {} };
}

/** Create (no id) or update (with id) a custom role. Owner only. */
export function saveRole(role: { id?: number; name: string; description?: string; permissions: Record<string, string[]> }) {
    const { id, ...body } = role;
    return id ? (api as any).update(`/workspaces/roles/${id}`, body) : api.create('/workspaces/roles', body);
}

export function deleteRole(id: number) {
    return api.delete(`/workspaces/roles/${id}`);
}

/** Give a member a custom role (or a built-in one with roleName) */
export function setMemberRole(userId: number, target: { role?: string; role_id?: number }) {
    return api.create('/workspaces/member/role', { user_id: userId, ...target });
}
